import { isUnsafeObjectKey } from "./text";
import type { AgeGroup, Arena, LocalizedLabel } from "./types";

/** Validation for administrator-editable matrix settings (mirrors schema.sql). */

const hasBothNames = (label: Partial<LocalizedLabel> | undefined): boolean =>
  typeof label?.no === "string" &&
  !!label.no.trim() &&
  typeof label?.en === "string" &&
  !!label.en.trim();

const isInt32 = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= -2147483648 && (value as number) <= 2147483647;

export type AgeGroupsValidationError = "count" | "key" | "name" | "range" | "continuous";

/** 1–8 contiguous, non-overlapping inclusive age ranges within 0–120. */
export function getAgeGroupsValidationError(groups: unknown): AgeGroupsValidationError | null {
  if (!Array.isArray(groups) || groups.length < 1 || groups.length > 8) return "count";
  const keys = new Set<string>();
  for (const [index, group] of (groups as AgeGroup[]).entries()) {
    if (
      !group ||
      typeof group.key !== "string" ||
      !/^[A-Za-z][A-Za-z0-9_-]*$/.test(group.key) ||
      isUnsafeObjectKey(group.key) ||
      keys.has(group.key)
    )
      return "key";
    keys.add(group.key);
    if (!hasBothNames(group.label)) return "name";
    if (
      !Number.isInteger(group.min) ||
      !Number.isInteger(group.max) ||
      group.min < 0 ||
      group.max > 120 ||
      group.min > group.max
    )
      return "range";
    const previous = (groups as AgeGroup[])[index - 1];
    if (previous && group.min !== previous.max + 1) return "continuous";
  }
  return null;
}

export type ArenasValidationError = "count" | "key" | "name" | "order";

export function getArenasValidationError(arenas: unknown): ArenasValidationError | null {
  if (!Array.isArray(arenas) || arenas.length === 0) return "count";
  const keys = new Set<string>();
  for (const arena of arenas as Arena[]) {
    if (
      !arena ||
      typeof arena.key !== "string" ||
      !arena.key.trim() ||
      arena.key !== arena.key.trim() ||
      isUnsafeObjectKey(arena.key) ||
      keys.has(arena.key)
    )
      return "key";
    keys.add(arena.key);
    if (!hasBothNames(arena.label)) return "name";
    if (!isInt32(arena.sortOrder)) return "order";
  }
  return null;
}

export function sortArenas(arenas: Arena[]): Arena[] {
  return [...arenas].sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key));
}
